import api from '@lib/axios';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ProjectSubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'cancelled'
  | 'suspended';

export interface ProjectSubscription {
  id: string;
  status: ProjectSubscriptionStatus;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  monthly_amount_inr: string;
}

/**
 * Shape returned by GET /projects (list).
 * `name` is the AiSensy-side name set at project creation.
 * `display_name_witzy` is the optional Witzy-side rename.
 */
export interface ProjectListItem {
  id: string;
  /** Primary display name — set by AiSensy at project creation. */
  name: string;
  /** Optional Witzy-side rename (null until PATCH /projects/:id is called). */
  display_name_witzy: string | null;
  aisensy_project_id: string | null;
  wa_number: string | null;
  /** Verified WhatsApp Business display name (shown to recipients). */
  wa_display_name?: string | null;
  is_whatsapp_verified: boolean;
  created_at: string;
  subscription: ProjectSubscription | null;
}

/** Extra fields returned only on GET /projects/:id */
export interface ProjectProviderDetails {
  credit_balance?: number;
  display_name?: string;
  [key: string]: unknown;
}

export interface ProjectDetail extends ProjectListItem {
  provider_details: ProjectProviderDetails | null;
}

export interface CreateProjectDto {
  display_name: string;
  fb_code?: string;
  waba_id?: string;
  phone_number_id?: string;
  waba_app_id?: string;
}

export interface ProjectCreated {
  id: string;
  name: string;
  display_name_witzy: string | null;
  aisensy_project_id: string | null;
  wa_number: string | null;
  is_whatsapp_verified: boolean;
  created_at: string;
  subscription: ProjectSubscription | null;
}

export interface InsufficientBalanceError {
  code: 'INSUFFICIENT_BALANCE';
  required_inr: number;
  current_balance_inr: number;
  deficit_inr: number;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * List all projects for the current organisation.
 * GET /projects — scoped to org via JWT; project_id query param is benign
 * (backend ignores it for this endpoint since it uses organization_id from the
 * JWT directly).
 */
export async function listProjects(): Promise<{ items: ProjectListItem[] }> {
  // The list endpoint is org-scoped by JWT and ignores project_id; opt
  // out of the interceptor so the request stays clean.
  const { data } = await api.get<{ data: ProjectListItem[] }>('/projects', {
    skipProjectId: true,
  });
  return { items: data.data };
}

/** @deprecated Use listProjects() instead */
export async function getAll(): Promise<ProjectListItem[]> {
  const result = await listProjects();
  return result.items;
}

/** Get a single project by ID including live provider details. */
export async function getProject(id: string): Promise<ProjectDetail> {
  const { data } = await api.get<{ data: ProjectDetail }>(`/projects/${id}`);
  return data.data;
}

/**
 * Create a new WhatsApp project (atomic wallet debit).
 * Throws a 402 error with body `{ code: 'INSUFFICIENT_BALANCE', ... }` if the
 * wallet balance is too low.
 */
export async function createProject(dto: CreateProjectDto): Promise<ProjectCreated> {
  const { data } = await api.post<{ data: ProjectCreated }>('/projects', dto);
  return data.data;
}

/** Rename the Witzy-side display name for a project. */
export async function renameProject(id: string, display_name_witzy: string): Promise<void> {
  await api.patch(`/projects/${id}`, { display_name_witzy });
}

/** Cancel a project subscription (end-of-period; data preserved). */
export async function cancelProject(id: string): Promise<void> {
  await api.delete(`/projects/${id}`);
}

/** Reactivate a cancelled / past_due / suspended project. */
export async function reactivateProject(
  id: string,
): Promise<{ status: string; current_period_end: string }> {
  const { data } = await api.post<{
    data: { status: string; current_period_end: string };
  }>(`/projects/${id}/reactivate`);
  return data.data;
}

// ---------------------------------------------------------------------------
// Paywall info (B.6.6)
// ---------------------------------------------------------------------------

export interface PaywallInfo {
  project_id: string;
  /** Decimal string (e.g. "750.00"). */
  overdue_amount_inr: string;
  /** ISO timestamp of when the subscription transitioned to past_due. */
  paywall_started_at: string | null;
  /** Distinct contacts who messaged in the last 24h (capped by paywall_started_at). */
  customer_count_24h: number;
}

/**
 * Fetch the data needed for the paywall page: overdue, customer count
 * over the last 24h, and the paywall start timestamp.
 *
 * Path-scoped (`/projects/:id/paywall-info`), so the active-project query
 * interceptor is suppressed via `skipProjectId`.
 */
export async function getPaywallInfo(id: string): Promise<PaywallInfo> {
  const { data } = await api.get<{ data: PaywallInfo }>(
    `/projects/${id}/paywall-info`,
    { skipProjectId: true },
  );
  return data.data;
}
